import React, { useState, useMemo } from "react";
import countries from "../data/countries";
import COUNTRY_COLORS from "../data/colors";
import { CONTINENTS, CATEGORY_META } from "../data/constants";

export default function RankingPage({ go, page = {} }) {
  const [sortKey, setSortKey] = useState(page.sort || "elevation");
  const [sortDir, setSortDir] = useState(page.dir || "desc");
  const [filterCont, setFilterCont] = useState(page.filter || "All");
  const [filterCat, setFilterCat] = useState("All");

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "name" || key === "peak" || key === "continent" ? "asc" : "desc"); }
  };

  const sorted = useMemo(() => {
    let data = [...countries];
    if (filterCont !== "All") data = data.filter((c) => c.continent === filterCont);
    if (filterCat !== "All") data = data.filter((c) => c.category === filterCat.toLowerCase());
    data.sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "string") { va = va.toLowerCase(); vb = vb.toLowerCase(); }
      return sortDir === "asc" ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
    return data;
  }, [sortKey, sortDir, filterCont, filterCat]);

  const Arrow = ({ col }) => sortKey !== col ? null : <span style={{ marginLeft: 3, fontSize: 9 }}>{sortDir === "asc" ? "▲" : "▼"}</span>;

  return (
    <div className="rp">
      <h1>Ranking</h1>
      <p>All {countries.length} country highpoints ranked. Click headers to sort.</p>
      <div className="ct" style={{ marginBottom: 20 }}>
        {CONTINENTS.map((c) => (
          <button key={c} className={`ctb ${filterCont === c ? "a" : ""}`} onClick={() => setFilterCont(c)}>{c}</button>
        ))}
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          style={{ marginLeft: 8, padding: "4px 8px", borderRadius: 6, border: "1px solid var(--brd)", background: "var(--card)", color: "var(--t1)", fontSize: 13, cursor: "pointer" }}
        >
          <option value="All">All Types</option>
          {Object.keys(CATEGORY_META).map((cat) => (
            <option key={cat} value={cat}>{CATEGORY_META[cat].label}</option>
          ))}
        </select>
      </div>
      <details className="grade-legend" style={{ marginBottom: 16, padding: "10px 14px", background: "var(--s2, #f5f5f5)", borderRadius: 8, fontSize: 13 }}>
        <summary style={{ cursor: "pointer", fontWeight: 600 }}>American Commitment Grade Guide</summary>
        <ul style={{ margin: "8px 0 0 0", paddingLeft: 18, lineHeight: 1.7 }}>
          <li><strong>Grade I:</strong> Less than half a day for the technical portion.</li>
          <li><strong>Grade II:</strong> Half a day for the technical portion.</li>
          <li><strong>Grade III:</strong> Most of a day for the technical portion.</li>
          <li><strong>Grade IV:</strong> A full day of technical climbing, generally at least 5.7.</li>
          <li><strong>Grade V:</strong> Typically requires an overnight on the route.</li>
          <li><strong>Grade VI:</strong> Two or more days of hard technical climbing.</li>
          <li><strong>Grade VII:</strong> Remote big walls climbed in alpine style.</li>
        </ul>
      </details>
      <div className="rtw">
        <table>
          <thead>
            <tr>
              <th style={{ width: 44 }}>#</th>
              <th className={sortKey === "name" ? "s" : ""} onClick={() => handleSort("name")}>Country<Arrow col="name" /></th>
              <th className={sortKey === "peak" ? "s" : ""} onClick={() => handleSort("peak")}>Highpoint<Arrow col="peak" /></th>
              <th className={sortKey === "elevation" ? "s" : ""} onClick={() => handleSort("elevation")} style={{ textAlign: "right" }}>Elev (m)<Arrow col="elevation" /></th>
              <th style={{ textAlign: "right" }}>Elev (ft)</th>
              <th className={sortKey === "continent" ? "s" : ""} onClick={() => handleSort("continent")}>Continent<Arrow col="continent" /></th>
              <th className={sortKey === "commitmentGrade" ? "s" : ""} onClick={() => handleSort("commitmentGrade")} style={{ textAlign: "center" }}>Grade<Arrow col="commitmentGrade" /></th>
              <th className={sortKey === "gain" ? "s" : ""} onClick={() => handleSort("gain")} style={{ textAlign: "right" }}>Gain (ft)<Arrow col="gain" /></th>
              <th className={sortKey === "distance" ? "s" : ""} onClick={() => handleSort("distance")} style={{ textAlign: "right" }}>Dist (mi)<Arrow col="distance" /></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c, i) => (
              <tr key={c.code}>
                <td className="rn">{i + 1}</td>
                <td>
                  <div className="cc2" onClick={() => go({ t: "detail", code: c.code, from: "ranking", filter: filterCont, sort: sortKey, dir: sortDir })}>
                    <div className="cd2" style={{ background: COUNTRY_COLORS[c.code] }} />
                    <span className="cn2">{c.name}</span>
                  </div>
                </td>
                <td className="pn">{c.peak}</td>
                <td className="ev" style={{ textAlign: "right" }}>{c.elevation.toLocaleString()}</td>
                <td className="ev" style={{ textAlign: "right", color: "var(--t2)" }}>{Math.round(c.elevation * 3.28084).toLocaleString()}</td>
                <td><span className="cb2">{c.continent}</span></td>
                <td style={{ textAlign: "center", fontWeight: 600, fontFamily: "monospace" }}>{c.commitmentGrade || "—"}</td>
                <td style={{ textAlign: "right", color: "var(--t2)" }}>{c.gain != null ? c.gain.toLocaleString() : "—"}</td>
                <td style={{ textAlign: "right", color: "var(--t2)" }}>{c.distance != null ? c.distance : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
